/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import BottomSpace from 'components/common/BottomSpace';
import { useTranslation } from 'react-i18next';
import useDirection from 'hooks/useDirection';
import { ROUTES } from 'constants/router';
import AvailableRoomMiniCard from 'components/card/AvailableRoomMiniCard';
import { useParams } from 'react-router';
import { getBuildingDetail, getListAvailableRoomInBuilding } from 'services/property.service';
import usePagination from 'hooks/usePagination';
import { delayNavigate, isSuccessCode, showData } from 'utils/common';
import { BuildingInfoType, RoomType } from '@/types/property.type';
import LoadMoreList from 'components/common/LoadMoreList';
import WrapperLayout from 'layouts/WrapperLayout';

export default function BuildingRoomContainer() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const { goTo } = useDirection();
  const { branchId, buildingId } = useParams();
  const { currentPage, limit, onNext } = usePagination();
  const total = useRef(0);
  const [buildingInfo, setBuildingInfo] = useState<BuildingInfoType>();
  const [availableRooms, setAvailableRooms] = useState<RoomType[]>([]);

  const getListAvailableRoom = async () => {
    const response = await getListAvailableRoomInBuilding({
      page: currentPage,
      limit,
      buildingId,
    });
    const { data, code } = response?.data;
    if (isSuccessCode(code)) {
      setAvailableRooms(data?.data);
      total.current = Number(data?.total);
    }
  };

  const getBuildingDetailInfo = async () => {
    const response = await getBuildingDetail({ buildingId, branchId });
    const { data, code } = response.data;
    if (isSuccessCode(code)) {
      setBuildingInfo(data);
    }
  };

  const initCallApi = async () => {
    await Promise.all([getListAvailableRoom(), getBuildingDetailInfo()]);
    delayNavigate(() => setIsFirstLoading(false));
  };

  const handleLoadMore = async (page: number) => {
    setIsLoading(true);
    const response = await getListAvailableRoomInBuilding({
      page: page,
      limit,
      buildingId,
    });
    const { data, code } = response?.data;
    if (isSuccessCode(code)) {
      const newList = availableRooms.concat(data?.data);
      setAvailableRooms(newList);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (currentPage > 1) {
      handleLoadMore(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    initCallApi();
  }, [buildingId, branchId]);

  return (
    <WrapperLayout title={buildingInfo?.name || ''}>
      <div className={styles.wrapper}>
        <p
          className="color-black font-xl font-bold"
          style={{ textAlign: 'left', lineHeight: 'var(--font-xxl)' }}
        >
          {t('title.up-coming-room')}
        </p>

        <div className={styles.listItem}>
          <LoadMoreList
            isFirstLoading={isFirstLoading}
            isLoading={isLoading}
            isEnded={availableRooms.length >= total.current}
            loadingElement={
              <>
                <AvailableRoomMiniCard isSkeleton />
                <AvailableRoomMiniCard isSkeleton />
                <AvailableRoomMiniCard isSkeleton />
              </>
            }
            onLoadMore={onNext}
          >
            {availableRooms.map((room) => {
              return (
                <AvailableRoomMiniCard
                  key={room.id}
                  price={Number(room?.price)}
                  roomName={room?.name}
                  availableDate={room?.latestBooking?.endDate}
                  onClick={goTo(
                    ROUTES.ROOM_DETAIL.replace(':roomId', String(room?.id))
                      .replace(':buildingId', String(room?.buildingId))
                      .replace(':branchId', String(room?.branchId)),
                  )}
                />
              );
            })}
          </LoadMoreList>
        </div>
        <BottomSpace />
      </div>
    </WrapperLayout>
  );
}
